/**
 * /app/$tenantSlug — the Me home (personal "Today").
 *
 * docs/58 Slice 2 split the old shared hub: the OPERATOR widgets
 * (accounting hero, workspace setup checklist, module quick-jump grid)
 * moved to the Run home (/overview). What stays here is everything about
 * ME — action-first, for every role:
 *
 *  - FirstRunBanners — trial countdown + mandatory-2FA enrolment prompt.
 *  - PasskeyNudge — "Add a passkey for faster sign-in" (dismissible).
 *  - AnnouncementsRibbon — pinned tenant-wide announcements (everyone reads).
 *  - Welcome header.
 *  - Quick actions — punch / apply leave / submit expense / view payslip,
 *    feature-gated, so the first thing an employee sees is what they DO.
 *  - Employee day-one checklist (recent joiners; steps tick from real data
 *    and the card hides when done or after 60 days — lib/day-one) + my
 *    in-progress onboarding card.
 *  - "My queue" (MyOpenItems) across helpdesk/travel/maternity/surveys.
 *  - "This fortnight" engagement (birthdays + work anniversaries).
 */

import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  BadgeIndianRupee,
  CalendarDays,
  CheckCircle2,
  Circle,
  House,
  Clock,
  ListChecks,
  type LucideIcon,
  ReceiptIndianRupee,
  ShieldCheck,
  X,
} from "lucide-react";
import { sessionHasFeature, sessionReady, useAuth } from "@/lib/auth";
import { isPoshOnly } from "@/lib/nav";
import { useQuery } from "@tanstack/react-query";
import { attendanceApi, poshApi } from "@/lib/api";
import { attendanceKeys } from "@/hooks/use-attendance";
import { poshKeys } from "@/hooks/use-posh";
import { useMyProfile } from "@/hooks/use-me-profile";
import {
  dayOneSteps as buildDayOneSteps,
  myOnboardingChecklist,
  hasCurrentPoshCertificate,
  hasRecentPunch,
  profileComplete,
  shouldShowDayOne,
} from "@/lib/day-one";
import { istTodayISO } from "@/lib/tax-return-periods";
import { hrKeys, useOnboardingInstance } from "@/hooks/use-hr";
import { hrApi } from "@/lib/api";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { PageHeader } from "@/components/shared/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AnnouncementsRibbon,
  announcementsQueryOptions,
} from "@/components/dashboard/announcements-ribbon";
import {
  EngagementWidget,
  engagementUpcomingQueryOptions,
} from "@/components/dashboard/engagement-widget";
import { FirstRunBanners } from "@/components/dashboard/first-run-banners";
import { PasskeyNudge } from "@/components/dashboard/passkey-nudge";
import { MyOpenItems } from "@/components/dashboard/my-open-items";
import { MeDetailsCard } from "@/components/details/me-details-card";
import { SetupChecklist } from "@/components/dashboard/setup-checklist";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/")({
  // A9 hover-prefetch. Fire-and-forget ONLY the two widget queries this
  // page issues unconditionally for every role/plan (announcements ribbon
  // + engagement fortnight). Everything else here is gated on role
  // (MyOpenItems) or plan feature (onboarding / hr_core) — the loader has
  // no auth context, and prefetching a feature-gated endpoint on a tenant
  // without the feature would trip the axios upgrade_required redirect ON
  // HOVER. prefetchQuery never throws, so navigation is never blocked.
  loader: ({ context }) => {
    // Only for a session that can read tenant data: a new joiner still on
    // the temporary password (or with no workspace picked yet) got 403s
    // here before the auth gate redirected (wave B polish).
    if (!sessionReady()) return;
    // POSH only (migration 162): announcements and birthdays are not on
    // the plan — a prefetch would 403 upgrade_required (→ Billing).
    if (!sessionHasFeature("hr_platform")) return;
    void context.queryClient.prefetchQuery(announcementsQueryOptions);
    void context.queryClient.prefetchQuery(engagementUpcomingQueryOptions);
  },
  component: DashboardHub,
});

const HR_ADMINS = ["company_admin", "hr_manager"];

// ---------------------------------------------------------------------------
// Quick actions (docs/56 U18 — employee action-first home). Each tile is
// something the caller DOES about themselves; feature-gated exactly like the
// day-one steps so it never lands on an upgrade placeholder. Payslips + My
// Time are always-on for every employee.
// ---------------------------------------------------------------------------

interface QuickAction {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /** Feature flag on /me.features required to surface; omit for always. */
  feature?: string;
  /** Only for a POSH only company (migration 162) — its home would
   *  otherwise have no action at all. */
  poshOnlyHome?: boolean;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: "punch",
    label: "Punch In / Out",
    href: "/app/$tenantSlug/my-time",
    icon: Clock,
    feature: "time_tracking",
  },
  {
    id: "leave",
    label: "Apply for Leave",
    href: "/app/$tenantSlug/leave/new",
    icon: CalendarDays,
    feature: "leave_management",
  },
  {
    id: "wfh",
    label: "Work from Home",
    href: "/app/$tenantSlug/wfh",
    icon: House,
    feature: "leave_management",
  },
  {
    id: "expense",
    label: "Submit an Expense",
    href: "/app/$tenantSlug/expenses/new",
    icon: ReceiptIndianRupee,
    feature: "expense",
  },
  {
    id: "payslip",
    label: "View Payslip",
    href: "/app/$tenantSlug/me/pay",
    icon: BadgeIndianRupee,
    // POSH only (migration 162): payslips are part of the platform.
    feature: "hr_platform",
  },
  {
    id: "posh-training",
    label: "Do your POSH training",
    href: "/app/$tenantSlug/posh/training",
    icon: ShieldCheck,
    feature: "posh",
    poshOnlyHome: true,
  },
];

function DashboardHub() {
  const { user } = useAuth();
  const tenantSlug = useTenantSlug();
  const name = user?.first_name?.trim() || "there";
  const role = user?.role ?? "";
  const features = user?.features ?? [];
  const isHRAdmin = HR_ADMINS.includes(role);

  // ----- Quick actions (feature-filtered) ------------------------------
  const quickActions = useMemo(
    () =>
      QUICK_ACTIONS.filter(
        (a) =>
          (!a.feature || features.includes(a.feature)) &&
          (!a.poshOnlyHome || isPoshOnly(features)),
      ),
    [features],
  );

  // ----- Employee day-one checklist (UX-14) ----------------------------
  // Non-HR-admins get a lean "find your way around" checklist. HR admins
  // get the workspace-setup checklist on the Run home (/overview) instead.
  const empDismissKey = `wr_day_one_dismissed_${user?.tenant_id ?? "none"}`;
  const [empDismissed, setEmpDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(empDismissKey) === "1";
  });
  function dismissDayOne() {
    try {
      window.localStorage.setItem(empDismissKey, "1");
    } catch {
      // localStorage full / disabled — fail closed, hide locally.
    }
    setEmpDismissed(true);
  }
  // Persona round 2026-10: each step ticks from the employee's own data
  // and the card only shows to a recent joiner (lib/day-one).
  const dayOneWanted = !isHRAdmin && !empDismissed;
  const today = istTodayISO();
  const monthAgo = istTodayISO(new Date(Date.now() - 30 * 86_400_000));
  const hasTime = features.includes("time_tracking");
  const hasPosh = features.includes("posh");
  const profileQ = useMyProfile();
  const myDaysQ = useQuery({
    queryKey: attendanceKeys.myDays(monthAgo, today),
    queryFn: ({ signal }) =>
      attendanceApi.listMyDays({ from: monthAgo, to: today }, signal),
    enabled: dayOneWanted && hasTime,
    retry: false,
  });
  const certsQ = useQuery({
    queryKey: poshKeys.certificates(),
    queryFn: ({ signal }) => poshApi.listCertificates(signal),
    enabled: dayOneWanted && hasPosh,
    retry: false,
  });
  // ----- "Your onboarding checklist" card (W2-04) ----------------------
  // GET /hr/onboarding is role-FILTERED in SQL (employee → own only). The
  // whole block is RequireFeature("hr_core") server-side, so gate on it.
  const hasHrCore = features.includes("hr_core");
  const myOnboardingQ = useQuery({
    queryKey: hrKeys.onboarding(),
    queryFn: ({ signal }) => hrApi.listEmployeeOnboarding(undefined, signal),
    enabled: hasHrCore,
    retry: false,
  });
  const myUserId = user?.user_id;
  // The joiner's own checklist — a not-started one too (a fresh joiner's
  // checklist is "not started" until the first task is ticked, so the card
  // never showed to the very people it is for).
  const myOnboarding = useMemo(
    () =>
      hasHrCore
        ? myOnboardingChecklist(myOnboardingQ.data?.onboarding, myUserId)
        : undefined,
    [hasHrCore, myUserId, myOnboardingQ.data],
  );
  const myInProgressOnboarding =
    myOnboarding && myOnboarding.status !== "completed" ? myOnboarding : undefined;
  const onboardingDetailQ = useOnboardingInstance(
    myInProgressOnboarding?.id ?? 0,
    Boolean(myInProgressOnboarding),
  );
  const rawPercent = onboardingDetailQ.data?.percent_done;
  const onboardingPercent = typeof rawPercent === "number" ? Math.round(rawPercent) : undefined;
  const showOnboardingCard = !isHRAdmin && Boolean(myInProgressOnboarding);
  const dayOneSteps = buildDayOneSteps({
    features,
    profileDone: profileComplete(profileQ.data),
    attendanceDone: hasRecentPunch(myDaysQ.data?.days),
    poshDone: hasCurrentPoshCertificate(certsQ.data?.certificates),
    onboarding: myOnboarding ?? null,
  });
  const showDayOne = shouldShowDayOne({
    isHRAdmin,
    dismissed: empDismissed,
    // A failed step read leaves that step unticked rather than hiding the
    // card; only the profile (joining date) must have arrived.
    loaded:
      profileQ.isSuccess &&
      (!hasTime || !myDaysQ.isPending) &&
      (!hasPosh || !certsQ.isPending) &&
      (!hasHrCore || !myOnboardingQ.isPending),
    hireDate: profileQ.data?.hire_date,
    today,
    steps: dayOneSteps,
  });
  const dayOneDone = dayOneSteps.filter((st) => st.done).length;


  return (
    <div className="space-y-6">
      <FirstRunBanners />

      <PageHeader
        title={`Welcome, ${name}`}
        description={
          user?.tenant
            ? `You're signed in to ${user.tenant.name}.`
            : "Your WorkRight dashboard."
        }
      />

      {/* Action-first: what an employee actually DOES, before any reading. */}
      {quickActions.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.id}
                to={a.href}
                className="group flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-foreground group-hover:text-primary">
                  {a.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}

      {/* HR asked this person for details: the one thing waiting on them, so first. */}
      <MeDetailsCard />

      {/* The passkey nudge and pinned announcements come after the welcome
          and the quick actions: above them they pushed the heading below
          the fold on a 320px phone. */}
      <PasskeyNudge />
      <AnnouncementsRibbon />

      {/* HR admins: the first-run checklist (a fresh admin lands HERE after
          registering). Self-hides for non-admins, once dismissed, or when
          every step is done. */}
      {isHRAdmin && <SetupChecklist variant="compact" />}

      {showDayOne && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-base">Welcome — Start Here</CardTitle>
              <CardDescription>
                {dayOneDone} of {dayOneSteps.length} done — a few things to
                finish in your first few weeks.
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={dismissDayOne}
              aria-label="Dismiss day-one checklist"
              className="h-7 w-7"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y divide-border">
              {dayOneSteps.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.href}
                    className="group flex items-start gap-3 py-3 transition-colors hover:bg-muted/40 sm:-mx-3 sm:rounded-md sm:px-3"
                  >
                    {item.done ? (
                      <CheckCircle2
                        className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
                        aria-label="Done"
                      />
                    ) : (
                      <Circle
                        className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                        aria-label="Not done yet"
                      />
                    )}
                    <div className="flex-1">
                      <div
                        className={
                          item.done
                            ? "text-sm font-medium text-muted-foreground line-through"
                            : "text-sm font-medium text-foreground group-hover:text-primary"
                        }
                      >
                        {item.label}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    </div>
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {showOnboardingCard && myInProgressOnboarding && (
        <Link
          to="/app/$tenantSlug/hr/onboarding/$id"
          params={{ tenantSlug, id: String(myInProgressOnboarding.id) }}
          className="group block"
        >
          <Card className="transition-colors group-hover:border-primary/40 group-hover:bg-accent/40">
            <CardHeader className="flex flex-row items-center gap-3 space-y-0">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <ListChecks className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base">
                  Your Onboarding Checklist
                  {typeof onboardingPercent === "number" && (
                    <span className="ml-1 font-normal text-muted-foreground">
                      ({onboardingPercent}% done)
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  Finish the tasks your team set up for your first few days.
                </CardDescription>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
            </CardHeader>
          </Card>
        </Link>
      )}

      <MyOpenItems />
      <EngagementWidget />
    </div>
  );
}
